/**
 * Zoom and pan for the docs' diagrams: a trigger on every figure, and one
 * full-screen view the page shares, mounted the first time a trigger is used.
 */
import { mount } from '@crown/framework/dom';
import type { Instruction } from '@crown/framework/ir';
import triggerTemplate from './zoom-trigger.tsrx';
import { ZoomModel } from './zoom-model';
import viewTemplate from './zoom-view.tsrx';

const ZoomView: (props: { readonly model: ZoomModel }) => Instruction = viewTemplate;
const ZoomTrigger: (props: { readonly onOpen: () => void }) => Instruction = triggerTemplate;

let shared: ZoomModel | null = null;

/** The page's one zoom view. */
function zoomView(): ZoomModel {
  if (shared) return shared;
  const model = new ZoomModel();
  mount(ZoomView({ model }), document.body);
  shared = model;
  return model;
}

function isSvgUrl(src: string): boolean {
  try {
    return new URL(src, location.href).pathname.toLowerCase().includes('.svg');
  } catch {
    return src.toLowerCase().includes('.svg');
  }
}

function addZoomTrigger(container: HTMLElement, onOpen: () => void): void {
  container.classList.add('docs-zoomable');
  mount(ZoomTrigger({ onOpen }), container);
}

/** Attach zoom triggers to all diagram figures, railroad blocks, and diagram previews inside `root`. */
export function initDiagramZoom(root: ParentNode): void {
  // SVG images inside markdown doc-figures
  for (const figure of root.querySelectorAll<HTMLElement>('.doc-figure')) {
    const img = figure.querySelector<HTMLImageElement>('img');
    if (img && isSvgUrl(img.src)) {
      addZoomTrigger(figure, () => {
        const caption = figure.querySelector('figcaption')?.textContent ?? img.alt ?? 'Diagram';
        void zoomView().openUrl(img.src, caption);
      });
    }
  }

  // Inline railroad diagrams (SVG already in DOM from renderRailroadDiagrams)
  for (const block of root.querySelectorAll<HTMLElement>('.docs-railroad-block')) {
    const attachToSvg = (svg: SVGElement) => {
      addZoomTrigger(block, () => zoomView().openInline(svg, 'Railroad diagram'));
    };
    const existing = block.querySelector<SVGElement>('svg');
    if (existing) {
      attachToSvg(existing);
    } else {
      // SVG not rendered yet (e.g. lazy or error) — observe for it once
      const observer = new MutationObserver(() => {
        const svg = block.querySelector<SVGElement>('svg');
        if (svg) {
          observer.disconnect();
          attachToSvg(svg);
        }
      });
      observer.observe(block, { childList: true, subtree: true });
    }
  }

  // Architecture-page diagram previews
  for (const preview of root.querySelectorAll<HTMLElement>('.docs-diagram-preview')) {
    const img = preview.querySelector<HTMLImageElement>('img');
    if (img) addZoomTrigger(preview, () => void zoomView().openUrl(img.src, img.alt || 'Deployment diagram'));
  }
}
